
import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { courseService } from "@/services/course.service"
import { Theme } from "@/types/api"
import { Button } from "@/components/ui/button"
import { Edit2, Trash2, Plus, Loader2 } from "lucide-react"
import { ThemeDialog } from "@/components/courses/ThemeDialog"
import { useToast } from "@/hooks/use-toast"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function ThemesPage() {
    const [themes, setThemes] = useState<Theme[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [editTheme, setEditTheme] = useState<Theme | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [deleteThemeSlug, setDeleteThemeSlug] = useState<string | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const { toast } = useToast()
    const PAGE_SIZE = 10

    const fetchThemes = async () => {
        setIsLoading(true)
        try {
            const response = await courseService.getThemes({
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
            })
            setThemes(response.results)
            setTotalCount(response.count)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить темы",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchThemes()
    }, [page])

    const handleDelete = async () => {
        if (!deleteThemeSlug) return
        setIsDeleting(true)
        try {
            await courseService.deleteTheme(deleteThemeSlug)
            toast({
                title: "Успешно",
                description: "Тема успешно удалена",
            })
            fetchThemes()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось удалить тему",
                variant: "destructive",
            })
        } finally {
            setIsDeleting(false)
            setDeleteThemeSlug(null)
        }
    }

    const handleEdit = (theme: Theme) => {
        setEditTheme(theme)
        setIsDialogOpen(true)
    }

    const handleCreate = () => {
        setEditTheme(null)
        setIsDialogOpen(true)
    }

    const columns: Column<Theme>[] = [
        {
            header: "Название",
            accessorKey: "title",
        },
        {
            header: "Слаг",
            accessorKey: "slug",
            className: "text-gray-500",
        },
        {
            header: "Создано",
            cell: (theme) => new Date(theme.created_at).toLocaleDateString(),
        },
        {
            header: "Действия",
            className: "text-right",
            cell: (theme) => (
                <div className="flex justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(theme)}
                    >
                        <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setDeleteThemeSlug(theme.slug)}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Темы</h1>
                    <p className="text-gray-600 mt-1">Управление темами курсов.</p>
                </div>
                <Button onClick={handleCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Создать тему
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Список тем</CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={themes}
                        isLoading={isLoading}
                        pageCount={Math.ceil(totalCount / PAGE_SIZE)}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                </CardContent>
            </Card>

            <ThemeDialog
                theme={editTheme}
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onSuccess={fetchThemes}
            />

            <AlertDialog open={!!deleteThemeSlug} onOpenChange={(open) => !open && setDeleteThemeSlug(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Это действие нельзя отменить.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Удаление...
                                </>
                            ) : (
                                "Удалить"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
